import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import DefaultLoader from "components/common/DefaultLoader";
import Notes from "components/common/Notes";
import * as submissionsService from "services/submissionsService";
import * as notesSelectors from "store/selectors/notes/notesSelectors";

const ReadOnlyNote = ({ title, value }) => {
  if (!value || value.trim() === "") {
    return <></>;
  }

  return (
    <Notes
      title={title}
      value={value}
      placeholder={""}
      disabled={true}
      rows={10}
      lg_width={6}
      md_width={12}
      growVertically
    />
  );
};

const PriorYearNotes = ({ submissionId, notesConfig }) => {
  const {
    data: submissionData,
    isFetching,
    isError,
  } = submissionsService.useRetrieveSubmissionQuery(
    { submissionId },
    { skip: !submissionId }
  );

  if (isFetching) {
    return (
      <Grid
        item
        xs={12}
        sx={{ display: "flex", justifyContent: "center", padding: "24px" }}
      >
        <DefaultLoader color={"#dc7f4c"} size={40} />
      </Grid>
    );
  }

  if (isError || !submissionData) {
    return (
      <Grid item xs={12}>
        <Typography color={"error"} variant={"body2"} sx={{ padding: "16px" }}>
          {"Unable to load notes for this year."}
        </Typography>
      </Grid>
    );
  }

  const notes = notesSelectors.selectNotes(submissionData?.state);

  const summaryNotes = notesConfig.filter(
    (note) =>
      note.section === "Summary" &&
      !["considerationsFromLastRenewal", "peerReview"].includes(note.id)
  );

  const additionalNotes = notesConfig.filter(
    (note) =>
      note.section === "Additional Notes" &&
      (notes?.[note.id] || "").trim() !== ""
  );

  const hasAnyContent =
    summaryNotes.some((n) => (notes?.[n.id] || "").trim() !== "") ||
    (notes?.considerationsFromLastRenewal || "").trim() !== "" ||
    (notes?.peerReview || "").trim() !== "" ||
    additionalNotes.length > 0;

  if (!hasAnyContent) {
    return (
      <Grid item xs={12}>
        <Typography
          variant={"body2"}
          color={"textSecondary"}
          sx={{ padding: "16px" }}
        >
          {"No notes recorded for this year."}
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {summaryNotes.map((note) => (
        <ReadOnlyNote
          key={note.id}
          title={note.label}
          value={notes?.[note.id] ?? ""}
        />
      ))}

      <ReadOnlyNote
        title={"Considerations from Last Renewal"}
        value={notes?.considerationsFromLastRenewal}
      />

      <ReadOnlyNote
        title={"Peer Review Commentary"}
        value={notes?.peerReview}
      />

      {additionalNotes.length > 0 && (
        <>
          <Grid item lg={12}>
            <Typography variant={"h5"}>{"Additional Notes"}</Typography>
          </Grid>
          {additionalNotes.map((note) => (
            <ReadOnlyNote
              key={note.id}
              title={note.label}
              value={notes?.[note.id] ?? ""}
            />
          ))}
        </>
      )}
    </>
  );
};

export default PriorYearNotes;
